import { Injectable } from '@angular/core'
import { Functions, httpsCallable } from '@angular/fire/functions'

/**
 * Redefine Property from API
 * Note: API uses PascalCase field names
 */
export interface RedefineProperty {
    PropertyId: number
    PropertyName: string
    Slug: string
    SectorName?: string
    Address?: string
    City?: string
    Province?: string
    Suburb?: string
    PostalCode?: string
    Latitude?: string  // Note: API returns as string
    Longitude?: string // Note: API returns as string
    MainImageURL?: string
    GLA?: string       // Gross Lettable Area (formatted string like "9,381")
    AvailableUnits?: number
    Space2Spec?: number
    Attributes?: any[]
    // From property details endpoint
    Description?: string
    Images?: RedefineImage[]
    Amenities?: RedefineAmenity[]
}

export interface RedefineImage {
    id: number
    url: string
    caption?: string
    isPrimary?: boolean
}

export interface RedefineAmenity {
    id: number
    name: string
    icon?: string
}

/**
 * Sector from Redefine API
 */
export interface RedefineSector {
    SectorId: number
    SectorName: string
    PropertyCount?: number
}

/**
 * Import result for a single property
 */
export interface RedefineImportResult {
    propertyId: number
    propertyName: string
    success: boolean
    action: 'created' | 'updated' | 'skipped'
    parkingKey?: string
    error?: string
}

/**
 * Import response from backend
 */
export interface RedefineImportResponse {
    success: boolean
    totalProcessed: number
    created: number
    updated: number
    failed: number
    results: RedefineImportResult[]
}

@Injectable({
    providedIn: 'root',
})
export class RedefineService {
    constructor(private functions: Functions) {}

    /**
     * Fetch all sectors from Redefine API
     */
    async getSectors(): Promise<RedefineSector[]> {
        try {
            const callable = httpsCallable(this.functions, 'redefine-getSectors', {
                timeout: 60000, // 1 minute
            })
            const result = await callable({})
            const data = result.data as { success: boolean; sectors: RedefineSector[] }
            if (data.success) {
                return data.sectors
            }
            throw new Error('Failed to fetch sectors')
        } catch (error: any) {
            console.error('[Redefine] Error fetching sectors:', error)
            throw error
        }
    }

    /**
     * Fetch all properties from Redefine API, optionally filtered by sector
     */
    async getProperties(sectorIds?: number[]): Promise<RedefineProperty[]> {
        try {
            const callable = httpsCallable(this.functions, 'redefine-getProperties', {
                timeout: 60000, // 1 minute
            })
            const result = await callable({ sectorIds })
            const data = result.data as { success: boolean; properties: RedefineProperty[] }
            if (data.success) {
                return data.properties
            }
            throw new Error('Failed to fetch properties')
        } catch (error: any) {
            console.error('[Redefine] Error fetching properties:', error)
            throw error
        }
    }

    /**
     * Import selected properties into a portfolio
     */
    async importProperties(portfolioKey: string, propertyIds: number[]): Promise<RedefineImportResponse> {
        try {
            const callable = httpsCallable(this.functions, 'redefine-importProperties', {
                timeout: 300000, // 5 minutes
            })
            const result = await callable({
                portfolioKey,
                propertyIds,
            })
            return result.data as RedefineImportResponse
        } catch (error: any) {
            console.error('[Redefine] Error importing properties:', error)
            throw error
        }
    }

    /**
     * Sync GPS coordinates for all imported Redefine properties in a portfolio
     * This only updates coordinates and geoHash, doesn't re-download images
     * Also syncs to root parkings collection to ensure map queries work
     * @param portfolioKey The portfolio to sync
     * @param forceSync If true, updates all parkings even if they already have correct coordinates
     */
    async syncCoordinates(portfolioKey: string, forceSync: boolean = false): Promise<{ success: boolean; updated: number; skipped: number; failed: number }> {
        try {
            const callable = httpsCallable(this.functions, 'redefine-syncCoordinates', {
                timeout: 120000, // 2 minutes
            })
            const result = await callable({ portfolioKey, forceSync })
            return result.data as { success: boolean; updated: number; skipped: number; failed: number }
        } catch (error: any) {
            console.error('[Redefine] Error syncing coordinates:', error)
            throw error
        }
    }
}
